import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { X, ExternalLink, User, Mail, Calendar, Tag, MessageSquare } from 'lucide-react'

interface ReplyDetailModalProps {
  isOpen: boolean
  onClose: () => void
  reply: any | null
}

export function ReplyDetailModal({ isOpen, onClose, reply }: ReplyDetailModalProps) {
  if (!reply) return null

  const formatEmailContent = (content: string) => {
    if (!content) return 'No content available'
    
    // Remove HTML tags and decode entities
    const textContent = content
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .trim()
    
    return textContent || 'No content available'
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString()
  }

  const getBadgeColor = (type: string, value: string) => {
    if (type === 'response_category') {
      if (value === 'interested' || value === 'Interested') return 'text-green-600 border-green-200 bg-green-50'
      if (value === 'not_interested' || value === 'Not Interested') return 'text-red-600 border-red-200 bg-red-50'
      if (value === 'neutral' || value === 'Neutral') return 'text-gray-600 border-gray-200 bg-gray-50'
      return 'text-blue-600 border-blue-200 bg-blue-50'
    }
    if (type === 'reply_category_type') {
      if (value === 'positive') return 'text-green-600 border-green-200 bg-green-50'
      if (value === 'negative') return 'text-red-600 border-red-200 bg-red-50'
      if (value === 'auto-reply') return 'text-gray-600 border-gray-200 bg-gray-50'
      if (value === 'ooo') return 'text-orange-600 border-orange-200 bg-orange-50'
      return 'text-blue-600 border-blue-200 bg-blue-50'
    }
    return 'text-gray-600 border-gray-200 bg-gray-50'
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <DialogTitle className="text-xl font-semibold">Reply Details</DialogTitle>
          {reply.app_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(reply.app_url, '_blank')}
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              View in Smartlead
            </Button>
          )}
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <User className="h-4 w-4" />
                Lead
              </div>
              <div className="space-y-1">
                <div className="font-medium">{reply.to_name || 'Unknown'}</div>
                <div className="text-sm text-muted-foreground font-mono">{reply.to_email || 'N/A'}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Mail className="h-4 w-4" />
                Sender
              </div>
              <div className="space-y-1">
                <div className="font-medium">{reply.from_name || 'Unknown'}</div>
                <div className="text-sm text-muted-foreground font-mono">{reply.from_email || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Campaign & Classification */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="text-sm font-medium text-muted-foreground">Campaign</div>
              <div className="space-y-1">
                <div className="font-medium text-sm">{reply.campaign_name || 'N/A'}</div>
                <div className="text-xs text-muted-foreground">ID: {reply.campaign_id || (reply.data as any)?.campaign_id || 'N/A'}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-sm font-medium text-muted-foreground">Classification</div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className={`text-xs ${getBadgeColor('response_category', reply.response_category)}`}>
                  {reply.response_category || 'Unknown'}
                </Badge>
                <Badge variant="outline" className={`text-xs ${getBadgeColor('reply_category_type', reply.reply_category_type)}`}>
                  {reply.reply_category_type || 'neutral'}
                </Badge>
                {reply.sentiment && (
                  <Badge variant="outline" className="text-xs text-gray-600 border-gray-200 bg-gray-50">
                    {reply.sentiment}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Timing */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Calendar className="h-4 w-4" />
              Timing
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Replied:</span>
                <div className="font-medium">{formatDate(reply.created_at)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Count:</span>
                <div className="font-medium">{reply.reply_count || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Reply Content */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <MessageSquare className="h-4 w-4" />
              Reply Content
            </div>
            <div className="bg-muted/50 p-4 rounded-lg border">
              <div className="text-sm whitespace-pre-wrap leading-relaxed">
                {formatEmailContent(reply.preview_text || reply.reply_body)}
              </div>
            </div>
          </div>

          {/* Additional Info */}
          {(reply.subject || reply.client_id) && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-muted-foreground">Additional Information</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {reply.subject && (
                  <div>
                    <span className="text-muted-foreground">Subject:</span>
                    <div className="font-medium">{reply.subject}</div>
                  </div>
                )}
                {reply.client_id && (
                  <div>
                    <span className="text-muted-foreground">Client ID:</span>
                    <div className="font-medium">{reply.client_id}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
